import classnames from 'classnames';
import React, { ReactElement } from 'react';
import { Button, Icon } from '../CiscoUi';
import { ButtonGroup } from '../ButtonGroup';
import { Tooltip, TooltipPosition } from '../Tooltip';
import styles from './index.module.css';
import { isToggleOptionSelected } from './utils/utils';

export type ToggleButtonGroupOptionTooltip = {
  text: string;
  position?: TooltipPosition;
};

export type ToggleButtonGroupOption<T> = {
  label: string;
  testId?: string;
  value: T;
  tooltip?: ToggleButtonGroupOptionTooltip;
};

export type ToggleButtonGroupOptions<T> = {
  left: ToggleButtonGroupOption<T>;
  right: ToggleButtonGroupOption<T>;
};

export type ToggleButtonGroupProps<T> = {
  options: ToggleButtonGroupOptions<T>;
  value: T;
  onChange: (value: T) => void;
  className?: string;
  disabled?: boolean;
};

export const ToggleButtonGroup = <T,>({
  options,
  value,
  onChange,
  className,
  disabled = false,
}: ToggleButtonGroupProps<T>): ReactElement => {
  const { left, right } = options;

  return (
    <ButtonGroup
      className={classnames(styles.root, className, {
        [styles.disabled]: disabled,
      })}
    >
      {[left, right].map((option) => {
        const isSelected = isToggleOptionSelected(value, option.value);

        return (
          <Button
            key={String(option.value)}
            type="button"
            className={classnames({ selected: isSelected })}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            testId={option.testId}
          >
            {option.label}
            {option.tooltip && (
              <span onClick={(e) => e.stopPropagation()}>
                <Tooltip
                  position={option.tooltip.position ?? 'down'}
                  id={`${option.value}-tooltip`}
                  text={option.tooltip.text}
                  size="medium"
                  className={classnames('half-margin-left', styles.tooltip)}
                >
                  <Icon name="info-outline" />
                </Tooltip>
              </span>
            )}
          </Button>
        );
      })}
    </ButtonGroup>
  );
};
